import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  InsertDriveFile as FileIcon,
  Visibility as ViewIcon,
  GetApp as ExcelIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import * as XLSX from 'xlsx';
import { describeQuestionTargets } from '../utils/interviewerTargets';
import { downloadSurveySav } from '../utils/surveySavExport';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SERVER_BASE = API_BASE.includes('/api') ? API_BASE.substring(0, API_BASE.indexOf('/api')) : API_BASE;

// Функция для перевода статусов модерации
const getStatusLabel = (status) => {
  const statusMap = {
    'pending': 'Ожидает',
    'completed': 'Завершён',
    'approved': 'Одобрен',
    'rejected': 'Отклонён',
    'under_review': 'На проверке'
  };
  return statusMap[status] || status;
};

// Функция для выбора цвета статуса
const getStatusColor = (status) => {
  const colorMap = {
    'approved': 'success',
    'completed': 'success',
    'rejected': 'error',
    'under_review': 'warning',
    'pending': 'default'
  };
  return colorMap[status] || 'default';
};

const getRespondentLabel = (response, idx, hideIdentity) => {
  if (!response.userId) return hideIdentity ? `Респондент ${idx + 1}` : 'Анонимно';
  if (hideIdentity) return `Респондент ${idx + 1}`;
  return response.user?.fullName || response.user?.email || `Респондент ${idx + 1}`;
};

// showInterviewers — только для админа: владелец-заказчик интервьюеров не видит
const escapeFormula = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
};

const uniqueExtraHeader = (used, base) => {
  let candidate = base;
  let n = 2;
  while (used.has(candidate)) { candidate = `${base} (${n})`; n += 1; }
  return candidate;
};

export default function SurveyStats({ survey, responses, hideRespondentIdentity = false, showInterviewers = false }) {
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);

  // Removed complex profile loading - quotas are assigned by server

  // Доп. вопросы интервьюеров видит только админ (сервер не отдаёт их другим ролям)
  const adminExtraQuestions = showInterviewers && Array.isArray(survey?.interviewerQuestions)
    ? survey.interviewerQuestions : [];

  // Разбивка по интервьюерам (только если в ответах есть метка интервьюера)
  const interviewerBreakdown = useMemo(() => {
    if (!showInterviewers) return [];
    // группируем по id интервьюера (тёзки не сливаются), имя — подпись
    const map = new Map();
    (responses || []).forEach((rw) => {
      if (rw && rw.interviewerId && rw.status !== 'rejected') {
        const e = map.get(rw.interviewerId) || { id: rw.interviewerId, name: rw.interviewerName || 'Интервьюер', count: 0 };
        e.count += 1;
        map.set(rw.interviewerId, e);
      }
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [responses, showInterviewers]);

  // Анализ статистики по квотам (должен быть до условных return)
  // Analyze quotas - simple approach: server assigns quotaId to each response
  const quotaStats = useMemo(() => {
    if (!survey || !survey.quotas || survey.quotas.length === 0 || !responses || responses.length === 0) {
      return null;
    }


    // Group responses by quotaId
    const quotaStats = survey.quotas.map((quota) => {
      // Find responses assigned to this quota
      const matchingResponses = responses.filter(r => r.quotaId === quota.id);
      
      // Use quota.filled as the source of truth (set by server when checking quota conditions)
      const totalFilled = quota.filled || 0;
      
      
      // If we have actual responses with quotaId, count by status
      // Otherwise, show aggregate from quota.filled
      const statusCounts = { approved: 0, rejected: 0, under_review: 0, completed: 0, pending: 0 };
      
      if (matchingResponses.length > 0) {
        matchingResponses.forEach(response => {
          const status = response.status || 'pending';
          if (statusCounts[status] !== undefined) {
            statusCounts[status]++;
          }
        });
      } else if (totalFilled > 0) {
        // No individual responses, but quota shows it's filled
        // Assume they're all completed (since they passed quota check)
        statusCounts.completed = totalFilled;
      }
      
      return {
        quota,
        totalResponses: totalFilled,
        statusCounts,
        responses: matchingResponses,
        isAnonymized: matchingResponses.length === 0 && totalFilled > 0
      };
    });
    
    return quotaStats;
  }, [survey, responses]);

  if (!survey || !responses || responses.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Нет данных для отображения статистики
        </Typography>
      </Box>
    );
  }

  // Функция для скачивания файла
  const handleDownloadFile = (filePath) => {
    const fileUrl = filePath.startsWith('/') ? `${SERVER_BASE}${filePath}` : filePath;
    window.open(fileUrl, '_blank');
  };

  // Сбор всех файлов из ответов
  const collectFiles = () => {
    const files = [];
    responses.forEach((response, respIdx) => {
      survey.questions?.forEach((question, qIdx) => {
        if (question.type === 'file') {
          const questionId = question.id || `q_${qIdx}`;
          const answer = response.answers?.[questionId];
          // Проверяем что ответ это объект с fileUrl
          if (answer && typeof answer === 'object' && answer.fileUrl) {
            files.push({
              respondent: getRespondentLabel(response, respIdx, hideRespondentIdentity),
              question: question.text,
              file: answer,
              responseId: response.id
            });
          }
        }
      });
    });
    return files;
  };

  // Функция экспорта в Excel
  const handleExportToExcel = () => {
    const questions = survey.questions || [];
    const headers = ['№', 'Респондент', 'Данные респондента', 'Дата', 'Статус'];
    const usedHeaders = new Set(headers);
    if (showInterviewers) {
      headers.push('Интервьюер');
      usedHeaders.add('Интервьюер');
    }
    questions.forEach((question) => {
      headers.push(question.text || '');
      usedHeaders.add(question.text || '');
    });
    if (showInterviewers) {
      adminExtraQuestions.forEach((q) => {
        const base = `Доп. вопрос: ${q.text}${q.archived ? ' (архив)' : ''} (${describeQuestionTargets(q, survey?.interviewers)})`;
        const header = uniqueExtraHeader(usedHeaders, base);
        usedHeaders.add(header);
        headers.push(header);
      });
    }

    const exportData = responses.map((response, idx) => {
      const isAnonymous = !response.userId;
      const user = response.user || {};
      const userName = getRespondentLabel(response, idx, hideRespondentIdentity);
      const userInfo = isAnonymous ? (hideRespondentIdentity ? '' : 'Анонимный ответ') : (
        hideRespondentIdentity
          ? [
              user.age ? `${user.age} лет` : '',
              user.gender || '',
              user.city || '',
              user.region || ''
            ].filter(Boolean).join(', ')
          : [
              user.fullName || '',
              user.email || '',
              user.age ? `${user.age} лет` : '',
              user.gender || '',
              user.city || '',
              user.region || ''
            ].filter(Boolean).join(', ')
      );

      const values = [
        idx + 1,
        userName,
        userInfo || 'Н/Д',
        response.completedAt ? new Date(response.completedAt).toLocaleString() : 'Н/Д',
        getStatusLabel(response.status || 'pending')
      ];
      if (showInterviewers) {
        values.push(response.interviewerId ? escapeFormula(response.interviewerName || 'Интервьюер') : '');
      }

      questions.forEach((question, qIdx) => {
        const questionId = question.id || `q_${qIdx}`;
        const answer = response.answers?.[questionId];

        if (question.type === 'file') {
          values.push(answer ? 'Файл загружен' : 'Нет файла');
        } else if (question.type === 'ranking') {
          values.push(Array.isArray(answer) ? answer.join(' → ') : 'Нет ответа');
        } else if (question.type === 'matrix') {
          if (answer && typeof answer === 'object') {
            const matrixStr = Object.entries(answer).map(([row, col]) => `${row}: ${col}`).join('; ');
            values.push(matrixStr || 'Нет ответа');
          } else {
            values.push('Нет ответа');
          }
        } else if (question.type === 'scale' || question.type === 'nps') {
          values.push(typeof answer === 'number' ? answer : (answer || 'Нет ответа'));
        } else if (Array.isArray(answer)) {
          values.push(answer.join(', '));
        } else {
          values.push(answer || 'Нет ответа');
        }
      });

      if (showInterviewers) {
        adminExtraQuestions.forEach((q) => {
          const a = response.interviewerAnswers ? response.interviewerAnswers[q.id] : undefined;
          values.push(escapeFormula(Array.isArray(a) ? a.join(', ') : (a === undefined || a === null ? '' : a)));
        });
      }

      return values;
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...exportData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ответы');
    
    // Скачиваем файл
    XLSX.writeFile(wb, `survey_${survey.id}_responses_${Date.now()}.xlsx`);
  };

  const handleExportToSav = () => {
    downloadSurveySav(survey, responses, {
      hideRespondentIdentity,
      showInterviewers
    });
  };

  const handleViewDetails = (response) => {
    setSelectedResponse(response);
    setDetailsDialogOpen(true);
  };

  // Анализ ответов по вопросам
  const analyzeQuestions = () => {
    const questionStats = {};
    
    survey.questions?.forEach((question, qIndex) => {
      const questionId = question.id || `q_${qIndex}`;
      questionStats[questionId] = {
        question: question.text,
        type: question.type,
        answers: {}
      };

      // Подсчет ответов
      responses.forEach(response => {
        const answer = response.answers?.[questionId];
        if (answer !== undefined && answer !== null) {
          if (question.type === 'ranking') {
            // Ranking: массив - показываем как есть, не агрегируем
            const rankingKey = Array.isArray(answer) ? answer.join(' → ') : String(answer);
            questionStats[questionId].answers[rankingKey] = 
              (questionStats[questionId].answers[rankingKey] || 0) + 1;
          } else if (question.type === 'matrix') {
            // Matrix: объект {row: col} - агрегируем по строкам
            if (typeof answer === 'object' && !Array.isArray(answer)) {
              Object.entries(answer).forEach(([row, col]) => {
                const key = `${row}: ${col}`;
                questionStats[questionId].answers[key] = 
                  (questionStats[questionId].answers[key] || 0) + 1;
              });
            }
          } else if (question.type === 'scale' || question.type === 'nps') {
            // Scale/NPS: числовое значение
            const numKey = String(answer);
            questionStats[questionId].answers[numKey] = 
              (questionStats[questionId].answers[numKey] || 0) + 1;
          } else if (Array.isArray(answer)) {
            // Для множественного выбора (multiple, ranking as fallback)
            answer.forEach(a => {
              questionStats[questionId].answers[a] = 
                (questionStats[questionId].answers[a] || 0) + 1;
            });
          } else {
            // Для одиночного выбора (single, dropdown, likert, text)
            const answerKey = String(answer);
            questionStats[questionId].answers[answerKey] = 
              (questionStats[questionId].answers[answerKey] || 0) + 1;
          }
        }
      });
    });

    return questionStats;
  };

  const questionStats = analyzeQuestions();

  // Конвертация данных для графиков
  const getChartData = (answers) => {
    return Object.entries(answers).map(([name, value]) => ({
      name,
      value
    }));
  };

  const files = collectFiles();

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1.5,
        mb: 2,
      }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Статистика ответов (всего: {responses.length})
        </Typography>
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          width: { xs: '100%', sm: 'auto' },
          '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
        }}>
          <Button
            variant="outlined"
            startIcon={<ExcelIcon />}
            onClick={handleExportToExcel}
            disabled={responses.length === 0}
            size="small"
          >
            Экспорт в Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportToSav}
            disabled={responses.length === 0}
            size="small"
          >
            Экспорт в SPSS
          </Button>
          {files.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<FileIcon />}
              onClick={() => {
                setSelectedFiles(files);
                setFileDialogOpen(true);
              }}
              size="small"
            >
              Файлы ({files.length})
            </Button>
          )}
        </Box>
      </Box>

      {interviewerBreakdown.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Ответы по интервьюерам
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Интервьюер</TableCell>
                <TableCell align="right">Ответов</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {interviewerBreakdown.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Grid container spacing={3}>
        {Object.entries(questionStats).map(([questionId, stats], index) => {
          const chartData = getChartData(stats.answers);
          
          if (chartData.length === 0) {
            return null;
          }

          return (
            <Grid item xs={12} key={questionId}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {stats.question}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Тип: {stats.type}
                </Typography>

                {/* Круговая диаграмма для выбора */}
                {(stats.type === 'single' || stats.type === 'multiple' || stats.type === 'dropdown' || stats.type === 'likert') && chartData.length <= 6 && (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                )}

                {/* Столбчатая диаграмма для большого количества вариантов */}
                {(stats.type === 'single' || stats.type === 'multiple' || stats.type === 'dropdown' || stats.type === 'likert') && chartData.length > 6 && (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                )}

                {/* Столбчатая диаграмма для scale и NPS */}
                {(stats.type === 'scale' || stats.type === 'nps') && (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                )}

                {/* Список для текстовых ответов и ranking */}
                {(stats.type === 'text' || stats.type === 'ranking') && (
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {chartData.slice(0, 10).map((item, idx) => (
                      <Typography key={idx} variant="body2" sx={{ mb: 1 }}>
                        • {item.name} ({item.value} раз)
                      </Typography>
                    ))}
                    {chartData.length > 10 && (
                      <Typography variant="caption" color="text.secondary">
                        ...и еще {chartData.length - 10} уникальных ответов
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Список для matrix */}
                {stats.type === 'matrix' && (
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {chartData.map((item, idx) => (
                      <Typography key={idx} variant="body2" sx={{ mb: 1 }}>
                        • {item.name}: {item.value} ответов
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Числовая статистика */}
                {stats.type === 'number' && (
                  <Box>
                    <Typography variant="body2">
                      Среднее: {(Object.entries(stats.answers).reduce((sum, [key, count]) => 
                        sum + (parseFloat(key) * count), 0) / responses.length).toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      Минимум: {Math.min(...Object.keys(stats.answers).map(k => parseFloat(k)))}
                    </Typography>
                    <Typography variant="body2">
                      Максимум: {Math.max(...Object.keys(stats.answers).map(k => parseFloat(k)))}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Статистика по квотам */}
      {quotaStats && quotaStats.length > 0 && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Статистика по квотам
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Распределение ответов по целевым квотам опроса
          </Typography>
          
          <Grid container spacing={2}>
            {quotaStats.map((stat, idx) => {
              const quota = stat.quota;
              const progress = quota.limit > 0 ? (stat.totalResponses / quota.limit) * 100 : 0;
              
              return (
                <Grid item xs={12} md={6} key={idx}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      border: '1px solid #e0e0e0',
                      bgcolor: progress >= 100 ? '#e8f5e9' : '#fff'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Квота #{idx + 1}
                      </Typography>
                      <Chip 
                        label={`${stat.totalResponses} / ${quota.limit}`}
                        size="small"
                        color={progress >= 100 ? 'success' : progress >= 80 ? 'warning' : 'default'}
                      />
                    </Box>

                    {/* Условия квоты */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Условия:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {quota.criteria?.dynamic && quota.criteria.dynamic.length > 0 && 
                          quota.criteria.dynamic.map((condition, cIdx) => 
                            Array.isArray(condition.answers) && condition.answers.map((answer, aIdx) => (
                              <Chip 
                                key={`${cIdx}-${aIdx}`}
                                label={answer}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            ))
                          )
                        }
                      </Box>
                      {quota.description && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                          {quota.description}
                        </Typography>
                      )}
                    </Box>

                    {/* Прогресс */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Заполнение</Typography>
                        <Typography variant="caption">{progress.toFixed(0)}%</Typography>
                      </Box>
                      <Box 
                        sx={{ 
                          width: '100%',
                          height: 8,
                          bgcolor: '#e0e0e0',
                          borderRadius: 4,
                          overflow: 'hidden'
                        }}
                      >
                        <Box 
                          sx={{ 
                            width: `${Math.min(progress, 100)}%`,
                            height: '100%',
                            bgcolor: progress >= 100 ? '#4caf50' : progress >= 80 ? '#ff9800' : '#2196f3',
                            transition: 'width 0.3s ease'
                          }} 
                        />
                      </Box>
                    </Box>

                    {/* Статистика по статусам */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Распределение по статусам:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {stat.statusCounts.approved > 0 && (
                          <Chip 
                            label={`Одобрено: ${stat.statusCounts.approved}`}
                            size="small"
                            color="success"
                          />
                        )}
                        {stat.statusCounts.completed > 0 && (
                          <Chip 
                            label={`Завершено: ${stat.statusCounts.completed}`}
                            size="small"
                            color="success"
                          />
                        )}
                        {stat.statusCounts.under_review > 0 && (
                          <Chip 
                            label={`На проверке: ${stat.statusCounts.under_review}`}
                            size="small"
                            color="warning"
                          />
                        )}
                        {stat.statusCounts.rejected > 0 && (
                          <Chip 
                            label={`Отклонено: ${stat.statusCounts.rejected}`}
                            size="small"
                            color="error"
                          />
                        )}
                        {stat.statusCounts.pending > 0 && (
                          <Chip 
                            label={`Ожидает: ${stat.statusCounts.pending}`}
                            size="small"
                            color="default"
                          />
                        )}
                      </Box>

                      {/* Детальная таблица респондентов по квоте */}
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ mt: 1 }}>
                          Респонденты ({stat.totalResponses}):
                        </Typography>
                        {stat.isAnonymized ? (
                          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              📊 {stat.totalResponses} {stat.totalResponses === 1 ? 'ответ' : stat.totalResponses < 5 ? 'ответа' : 'ответов'}
                            </Typography>
                          </Box>
                        ) : stat.responses.length > 0 ? (
                          <TableContainer sx={{ maxHeight: 200, overflowX: 'auto' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Респондент</TableCell>
                                  <TableCell>Дата</TableCell>
                                  <TableCell>Статус</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {stat.responses.map((response, rIdx) => {
                                  const user = response.user || {};
                                  const isAnonymous = !response.userId;
                                  const userName = getRespondentLabel(response, rIdx, hideRespondentIdentity);
                                  
                                  return (
                                    <TableRow key={rIdx}>
                                      <TableCell>
                                        <Typography variant="caption" display="block">{userName}</Typography>
                                        {!isAnonymous && user.age && (
                                          <Typography variant="caption" color="text.secondary">
                                            {user.age} лет, {user.gender || 'Н/Д'}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="caption">
                                          {response.completedAt ? new Date(response.completedAt).toLocaleDateString() : 'Н/Д'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={getStatusLabel(response.status || 'pending')}
                                          size="small"
                                          color={getStatusColor(response.status || 'pending')}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Пока нет ответов
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Таблица с детальными ответами */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Детальные ответы
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>№</TableCell>
                <TableCell>Респондент</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>Время (сек)</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {responses.map((response, idx) => {
                const user = response.user || {};
                const isAnonymous = !response.userId;
                const userName = getRespondentLabel(response, idx, hideRespondentIdentity);
                
                return (
                  <TableRow key={response.id || idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{userName}</Typography>
                        {!isAnonymous && user.age && <Typography variant="caption" color="text.secondary">{user.age} лет</Typography>}
                        {!isAnonymous && user.gender && <Typography variant="caption" color="text.secondary"> • {user.gender}</Typography>}
                        {!isAnonymous && user.city && <Typography variant="caption" display="block" color="text.secondary">{user.city}{user.region ? `, ${user.region}` : ''}</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {response.completedAt ? new Date(response.completedAt).toLocaleString() : 'Н/Д'}
                    </TableCell>
                    <TableCell>
                      {response.timeSpent || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(response.status || 'pending')} 
                        size="small"
                        color={getStatusColor(response.status || 'pending')}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleViewDetails(response)}>
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Диалог просмотра файлов */}
      <Dialog
        open={fileDialogOpen}
        onClose={() => setFileDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Загруженные файлы ({selectedFiles.length})</DialogTitle>
        <DialogContent>
          <List>
            {selectedFiles.map((item, idx) => (
              <ListItem
                key={idx}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleDownloadFile(item.file.fileUrl || item.file)}
                    color="primary"
                  >
                    <DownloadIcon />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  <FileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={item.file.originalName || (typeof item.file === 'string' ? item.file.split('/').pop() : 'Файл')}
                  secondary={
                    <>
                      <Typography variant="caption" display="block">
                        {item.respondent}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {item.question}
                      </Typography>
                      {item.file.size && (
                        <Typography variant="caption" display="block">
                          Размер: {(item.file.size / 1024).toFixed(2)} KB
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFileDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог детального просмотра ответа */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Детальный просмотр ответа</DialogTitle>
        <DialogContent>
          {selectedResponse && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Информация о респонденте
              </Typography>
              {selectedResponse.user ? (
                <Box sx={{ mb: 2 }}>
                  {!hideRespondentIdentity && selectedResponse.user.fullName && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Имя:</strong> {selectedResponse.user.fullName}
                    </Typography>
                  )}
                  {!hideRespondentIdentity && selectedResponse.user.email && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Email:</strong> {selectedResponse.user.email}
                    </Typography>
                  )}
                  {selectedResponse.user.age && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Возраст:</strong> {selectedResponse.user.age} лет
                    </Typography>
                  )}
                  {selectedResponse.user.gender && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Пол:</strong> {selectedResponse.user.gender}
                    </Typography>
                  )}
                  {(selectedResponse.user.city || selectedResponse.user.region) && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Местоположение:</strong> {selectedResponse.user.city}{selectedResponse.user.region ? `, ${selectedResponse.user.region}` : ''}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                  {hideRespondentIdentity ? 'Респондент' : `ID: ${selectedResponse.userId || 'Анонимно'}`}
                </Typography>
              )}
              <Typography variant="body2" gutterBottom>
                <strong>Дата прохождения:</strong> {selectedResponse.completedAt ? new Date(selectedResponse.completedAt).toLocaleString() : 'Н/Д'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Время прохождения:</strong> {selectedResponse.timeSpent ? `${selectedResponse.timeSpent} сек` : 'Н/Д'}
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                <strong>Статус:</strong> <Chip label={getStatusLabel(selectedResponse.status || 'pending')} size="small" color={getStatusColor(selectedResponse.status || 'pending')} />
              </Typography>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Ответы на вопросы
              </Typography>
              <List>
                {survey.questions?.map((question, qIdx) => {
                  const questionId = question.id || `q_${qIdx}`;
                  const answer = selectedResponse.answers?.[questionId];
                  
                  return (
                    <ListItem key={questionId} divider>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          {question.text}
                        </Typography>
                        {question.type === 'file' && answer ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              icon={<FileIcon />}
                              label={typeof answer === 'object' && answer.originalName ? answer.originalName : (typeof answer === 'string' ? answer.split('/').pop() : 'Файл')}
                              onClick={() => handleDownloadFile(typeof answer === 'object' ? answer.fileUrl : answer)}
                              clickable
                              color="primary"
                              size="small"
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadFile(typeof answer === 'object' ? answer.fileUrl : answer)}
                              color="primary"
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Box>
                        ) : question.type === 'ranking' && Array.isArray(answer) ? (
                          <Typography variant="body2" color="text.secondary">
                            {answer.join(' → ')}
                          </Typography>
                        ) : question.type === 'matrix' && typeof answer === 'object' && !Array.isArray(answer) ? (
                          <Box>
                            {Object.entries(answer).map(([row, col], idx) => (
                              <Typography key={idx} variant="body2" color="text.secondary">
                                {row}: {col}
                              </Typography>
                            ))}
                          </Box>
                        ) : Array.isArray(answer) ? (
                          <Typography variant="body2" color="text.secondary">
                            {answer.join(', ')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {answer !== null && answer !== undefined ? String(answer) : 'Нет ответа'}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
              {showInterviewers && adminExtraQuestions.length > 0 && selectedResponse.interviewerAnswers && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Дополнительные вопросы интервьюера{selectedResponse.interviewerName ? ` (${selectedResponse.interviewerName})` : ''}
                  </Typography>
                  <List>
                    {adminExtraQuestions.map((q) => {
                      const a = selectedResponse.interviewerAnswers[q.id];
                      return (
                        <ListItem key={q.id} divider>
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                              {q.text}{q.archived ? ' (архив)' : ''} <Typography component="span" variant="caption" color="text.secondary">({describeQuestionTargets(q, survey?.interviewers)})</Typography>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {Array.isArray(a) ? a.join(', ') : (a !== null && a !== undefined && a !== '' ? String(a) : 'Нет ответа')}
                            </Typography>
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
